'use client';
import { useTranslations } from 'next-intl';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { CUSTOMER_NOTIFICATIONS_LIST_LIMIT } from '@/entities/customer-notification/api/customer-notifications/constants';
import { useCustomerNotificationsList } from '@/features/news/useCustomerNotificationsList';
import { useNewsUnread } from '@/features/news/useNewsUnread';
import { useGetPageUrl } from '@/schema/useGetPageUrl';
import { Link } from '@/shared/i18n/navigation';
import { Badge } from '@/shared/ui/badge';
import { Icon } from '@/shared/ui/icon';

const ContactLinkLabel = () => {
    const t = useTranslations();
    const { unreadCount } = useNewsUnread();
    const { items } = useCustomerNotificationsList({ limit: CUSTOMER_NOTIFICATIONS_LIST_LIMIT });
    const count = items.length ? unreadCount(items) : 0;

    return (
        <div className="menu__link-with-badge">
            <span>{t('MENU_LINKS.CONTACT')}</span>
            {count > 0 ? (
                <Badge
                    variant="error"
                    className="menu__contact-badge"
                    data-testid="menu-contact-unread-badge"
                >
                    {count > 9 ? '9+' : count}
                </Badge>
            ) : null}
        </div>
    );
};

type MenuLink = {
    icon: (svgProps: ComponentPropsWithoutRef<'svg'>) => ReactNode;
    label: ReactNode;
    href: string;
};

const useLinks = (): MenuLink[] => {
    const getPageUrl = useGetPageUrl();
    const t = useTranslations();

    return [
        {
            icon: (svgProps: ComponentPropsWithoutRef<'svg'>) => <Icon name="bag" {...svgProps} />,
            label: t('MENU_LINKS.PRODUCTS'),
            href: getPageUrl('home'),
        },
        {
            icon: (svgProps: ComponentPropsWithoutRef<'svg'>) => <Icon name="cart" {...svgProps} />,
            label: t('MENU_LINKS.CART'),
            href: getPageUrl('cart'),
        },
        {
            icon: (svgProps: ComponentPropsWithoutRef<'svg'>) => <Icon name="phone" {...svgProps} />,
            label: <ContactLinkLabel />,
            href: getPageUrl('contact'),
        },
        {
            icon: (svgProps: ComponentPropsWithoutRef<'svg'>) => <Icon name="wheat" {...svgProps} />,
            label: t('MENU_LINKS.ALLERGENS_LIST'),
            href: getPageUrl('alergens'),
        },
    ];
};

export const MenuLinks = () => {
    const links = useLinks();

    return (
        <nav className="menu__links">
            <ul>
                {links.map((link, index) => {
                    return (
                        <li key={index}>
                            <Link href={link.href} className="menu__link">
                                <link.icon width={30} height={30} className="menu__link-icon" />

                                <span className="menu__link-label">{link.label}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};
